'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'

import { Col } from '@/components/ui/col'
import type { BlogsApiItem, TagApiItem } from '@/lib/api/blogs'
import { useLocale, useTranslations } from 'next-intl'
import { getHomeRoutes } from '@/lib/routes/home-routes'

const GRID_INITIAL_VISIBLE = 5
const GRID_LOAD_MORE_STEP = 6

type Props = {
  blogs: BlogsApiItem[]
  filterTags?: TagApiItem[]
}

export function InsightsPerspectivesGrid({ blogs }: Props) {
  const t = useTranslations('insightsPerspectivesPage')
  const locale = useLocale()
  const routes = getHomeRoutes(locale)
  const [visibleCount, setVisibleCount] = useState(GRID_INITIAL_VISIBLE)

  const filtered = blogs

  const visibleBlogs = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount],
  )

  const canLoadMore = blogs.length > visibleCount

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
        <div>
          <h2 className="text-2xl font-bold text-charcoal-900 tracking-tight mb-2">
            {t('index.title')}
          </h2>
          <p className="text-charcoal-600 font-light text-sm">{t('index.desc')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {visibleBlogs.map((blog) => {
          const isTrend = blog.tags?.some((tag) => tag.slug === 'trends')
          const borderHover = isTrend ? 'hover:border-brand-red' : 'hover:border-gray-400'
          const tagClass = isTrend
            ? 'px-2 py-1 bg-red-50 border border-brand-red/20 text-fs-xs font-mono font-bold text-brand-red tracking-widest uppercase rounded-sm'
            : 'px-2 py-1 bg-white border border-gray-200 text-fs-xs font-mono font-bold text-charcoal-700 tracking-widest uppercase rounded-sm'

          return (
            <Link
              key={blog.documentId ?? String(blog.id)}
              href={`${routes.insightsPerspectives}/${blog.slug}`}
              className={`group flex flex-col h-full bg-white border border-gray-200 ${borderHover} transition-all duration-300 shadow-sm rounded-lg hover:shadow-md hover:-translate-y-1 active:translate-y-0 active:bg-gray-50 overflow-hidden`}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                <span className={tagClass}>{blog.tags?.[0]?.name ?? t('featured.tag')}</span>
                <span className="text-fs-sm font-mono text-charcoal-400">{blog.author_name ?? 'Dcorp'}</span>
              </div>
              <Col className="p-8 flex-grow">
                <h3 className="text-xl font-bold text-charcoal-900 mb-4 leading-snug group-hover:text-brand-red transition-colors">
                  {blog.title}
                </h3>
                <p className="text-sm text-charcoal-600 font-light leading-relaxed flex-grow">
                  {blog.short_description}
                </p>

                <div className="flex items-center text-fs-sm font-bold text-charcoal-900 uppercase tracking-widest mt-8 group-hover:text-brand-red transition-colors">
                  <span>{t('articles.read')}</span>
                  <span className="ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300">
                    &rarr;
                  </span>
                </div>
              </Col>
            </Link>
          )
        })}

        {canLoadMore && (
          <button
            type="button"
            onClick={() =>
              setVisibleCount((c) => Math.min(c + GRID_LOAD_MORE_STEP, filtered.length))
            }
            className="flex w-full flex-col items-center justify-center bg-gray-100 border border-gray-200 min-h-[300px] cursor-pointer rounded-lg transition-colors hover:bg-gray-200 active:bg-gray-300 group"
          >
            <span className="w-10 h-10 border border-charcoal-400 rounded-full flex items-center justify-center mb-4 group-hover:border-brand-red transition-colors">
              <span className="w-2 h-2 bg-charcoal-400 rounded-full group-hover:bg-brand-red transition-colors" />
            </span>
            <span className="text-xs font-bold text-charcoal-600 uppercase tracking-widest group-hover:text-brand-red transition-colors">
              {t('articles.load_more')}
            </span>
          </button>
        )}
      </div>
    </>
  )
}
